// Obra › Clima: el clima de la obra pensado para una constructora.
//   Ahora · Pronóstico (48 h + 16 días con semáforo de trabajo) · Planificador del mes · ¿Cuándo hago…? (hormigonado,
//   impermeabilización, pintura, suelos, grúa) · Historial (cruzado con la actividad de la IA) · Reporte PDF · Alertas.
// Datos: Open-Meteo (gratis, sin clave, con caché). Reglas y textos: clima/reglasClima.js (plantillas fijas, sin IA).
// Ubicación: la de la obra (el cliente la marca en el mapa si no hay).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, RefreshCw, AlertCircle } from 'lucide-react';
import { WhatsAppButton } from '../../../whatsapp/components/WhatsAppComponents';
import { resolveLocation } from '../IADashboard/weather';
import { UbicacionObraPicker, UbicacionObraModal, CambiarUbicacionLink } from '../../../obras/components/UbicacionObra';
import R from './clima/reglasClima';
import { pronostico, hoyObra, mensajeClima } from './clima/meteo';
import { leerConfig } from './clima/configClima';
import { formatTime24 } from '../../../utils/formatTime24';
import { Pestanas, Semaforo, Icono3D, Cargando, CLIMA_CSS, grados } from './clima/ui';
import Ahora from './clima/Ahora';
import Pronostico from './clima/Pronostico';
import Planificador from './clima/Planificador';
import Tareas from './clima/Tareas';
import Historial from './clima/Historial';
import Reporte from './clima/Reporte';
import AlertasClima from './clima/AlertasClima';
import Umbrales from './clima/Umbrales';

const TABS = [
  { key: 'ahora', label: 'Ahora', emoji: '🌤️' },
  { key: 'pronostico', label: 'Pronóstico', emoji: '📈' },
  { key: 'plan', label: 'Planificador del mes', emoji: '🗓️' },
  { key: 'tareas', label: '¿Cuándo hago…?', emoji: '🧱' },
  { key: 'historial', label: 'Historial', emoji: '🕑' },
  { key: 'reporte', label: 'Reporte PDF', emoji: '📄' },
  { key: 'alertas', label: 'Alertas', emoji: '🔔' },
];
const tabInicial = () => {
  try {
    const q = new URLSearchParams(window.location.search).get('clima');
    if (TABS.some((t) => t.key === q)) return q;
    const g = sessionStorage.getItem('clima:tab');
    if (TABS.some((t) => t.key === g)) return g;
  } catch (e) { /* */ }
  return 'ahora';
};
const esInterno = () => {
  try {
    const q = new URLSearchParams(window.location.search).get('interno');
    if (q === '1') localStorage.setItem('lisualInterno', '1');
    if (q === '0') localStorage.removeItem('lisualInterno');
    return localStorage.getItem('lisualInterno') === '1';
  } catch (e) { return false; }
};

const WeatherComponent = ({ user, empresa }) => {
  // Ubicación de la obra (Firebase › cámara › caché). undefined = buscando, null = no hay: el cliente la marca en el mapa
  const [loc, setLoc] = useState(undefined);
  const [cambiar, setCambiar] = useState(false);
  const [pron, setPron] = useState(null);
  const [error, setError] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState(tabInicial);
  const [tarea, setTarea] = useState('hormigonado');
  const [umbralesOpen, setUmbralesOpen] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const interno = useMemo(esInterno, []);

  // 1) dónde está la obra
  useEffect(() => {
    let alive = true;
    setLoc(undefined);
    resolveLocation({ coords: user?.coordinates, feedId: user?.feedID, cam: user?.identifier })
      .then((l) => alive && setLoc(l))
      .catch(() => alive && setLoc(null));
    return () => { alive = false; };
  }, [user?.feedID, user?.identifier, user?.coordinates?.lat, user?.coordinates?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2) criterios de la obra (umbrales) y reporte mensual
  useEffect(() => {
    let alive = true;
    leerConfig(user?.feedID).then((c) => alive && setCfg(c)).catch(() => alive && setCfg({ umbrales: R.UMBRALES_DEFAULT, reporteMensual: R.sanearReporte({}), origen: 'local' }));
    return () => { alive = false; };
  }, [user?.feedID]);

  // 3) con la ubicación, el pronóstico (incluye ahora, 2 días pasados y 16 adelante)
  useEffect(() => {
    let alive = true;
    if (!loc) { setPron(null); return () => { alive = false; }; }
    setError(null);
    pronostico(loc).then((p) => alive && setPron(p)).catch((e) => { if (!alive) return; setError(mensajeClima(e)); setTimeout(() => alive && setRecarga((n) => n + 1), 30000); });
    return () => { alive = false; };
  }, [loc, recarga]);

  const ix = useMemo(() => (pron ? R.indexar(pron) : null), [pron]);
  const U = cfg ? cfg.umbrales : R.UMBRALES_DEFAULT;
  const hoy = pron ? hoyObra(pron.offset) : null;
  const ir = useCallback((k) => { setTab(k); try { sessionStorage.setItem('clima:tab', k); } catch (e) { /* */ } window.scrollTo?.({ top: 0, behavior: 'smooth' }); }, []);
  const irTarea = useCallback((k) => { setTarea(k); ir('tareas'); }, [ir]);

  // Resumen de arriba: hoy, mañana y la semana
  const resumen = useMemo(() => {
    if (!ix || !hoy) return null;
    const dias = [0, 1, 2, 3, 4, 5, 6].map((k) => R.addDias(hoy, k));
    const ev = dias.map((f) => R.evaluarDia(ix, f, U));
    const hab = ev.filter((e) => e.laborable && e.estado);
    return { hoy: ev[0], manana: ev[1], aptos: hab.filter((e) => e.estado !== 'no').length, habiles: hab.length, noAptos: hab.filter((e) => e.estado === 'no').length };
  }, [ix, hoy, U]);

  if (loc === null) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <Icono3D kind="parcial" size={48} />
          <div>
            <p className="text-lg font-semibold text-gray-900">Para ver el clima de tu obra, marcá dónde está</p>
            <p className="text-sm text-gray-600">Con la ubicación te mostramos el pronóstico, qué días se puede trabajar, los mejores días para hormigonar y el historial para justificar demoras.</p>
          </div>
        </div>
        <UbicacionObraPicker feedId={user?.feedID} cam={user?.identifier} initial={null} direccion="" onSaved={(l) => setLoc(l)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-6 rounded-2xl border border-red-100">
        <div className="flex items-center gap-3">
          <AlertCircle className="text-red-500 w-6 h-6" />
          <p className="text-red-700 font-medium">{error}</p>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => setRecarga((x) => x + 1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-700 hover:underline"><RefreshCw className="w-4 h-4" />Reintentar</button>
          <CambiarUbicacionLink onClick={() => setCambiar(true)} />
        </div>
        <UbicacionObraModal open={cambiar} onClose={() => setCambiar(false)} feedId={user?.feedID} cam={user?.identifier}
          initial={loc} direccion={loc?.direccion || ''} onSaved={(l) => { setCambiar(false); setLoc(l); }} />
        <div className="mt-4">
          <WhatsAppButton message={`Hola, estoy teniendo problemas para ver el clima en LisualPro. Mi ID es ${user?.identifier}`} className="w-full sm:w-auto">Contactar Soporte</WhatsAppButton>
        </div>
      </div>
    );
  }

  if (!ix || !cfg || !hoy) {
    return <div className="bg-white rounded-2xl border border-gray-200"><Cargando txt="Cargando el clima de la obra…" className="py-24" /></div>;
  }

  const a = ix.actual || {};
  const kindAhora = R.tipoTiempo(a.weather_code, a.precipitation || 0, a.is_day === 0) || 'nublado';
  const tabs = TABS.map((t) => (t.key === 'pronostico' && resumen?.noAptos ? { ...t, badge: resumen.noAptos } : t));

  return (
    <div className="relative w-full space-y-4">
      <style>{CLIMA_CSS}</style>
      <UbicacionObraModal open={cambiar} onClose={() => setCambiar(false)} feedId={user?.feedID} cam={user?.identifier}
        initial={loc} direccion={loc?.direccion || ''} onSaved={(l) => { setCambiar(false); setLoc(l); }} />
      <Umbrales open={umbralesOpen} onClose={() => setUmbralesOpen(false)} feedId={user?.feedID} U={U} onGuardado={setCfg} />

      {/* Encabezado: dónde, ahora y la semana en una línea */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-3 sm:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Icono3D kind={kindAhora} size={46} />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Clima de la obra <span className="text-gray-400 font-semibold">· {grados(a.temperature_2m)}</span></h2>
              <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1.5 min-w-0">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{loc?.direccion || loc?.label || 'Ubicación de la obra'}</span>
                <CambiarUbicacionLink onClick={() => setCambiar(true)} className="shrink-0" />
              </p>
            </div>
          </div>
          {resumen && (
            <div className="flex flex-wrap items-center gap-1.5">
              {[['Hoy', resumen.hoy], ['Mañana', resumen.manana]].map(([t, e]) => (e.estado && e.laborable
                ? <Semaforo key={t} estado={e.estado}>{t}: {R.ESTADO_TXT[e.estado].toLowerCase()}</Semaforo>
                : <span key={t} className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{t} ({R.fechaCorta(e.fecha).split(' ')[0]}): no se trabaja</span>))}
              <span className="text-xs font-semibold text-gray-700 bg-gray-100 rounded-full px-2.5 py-1">Semana: {resumen.aptos} de {resumen.habiles} días para trabajar</span>
              <button type="button" onClick={() => { setPron(null); setRecarga((x) => x + 1); }} title={`Actualizado ${formatTime24(pron.traido)} · Open-Meteo`} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100" aria-label="Actualizar"><RefreshCw className="w-4 h-4" /></button>
            </div>
          )}
        </div>
        <div className="mt-3">
          <Pestanas tabs={tabs} activa={tab} onCambiar={ir} />
        </div>
      </div>

      {tab === 'ahora' && <Ahora ix={ix} loc={loc} user={user} U={U} hoy={hoy} interno={interno} onIr={ir} />}
      {tab === 'pronostico' && <Pronostico ix={ix} U={U} hoy={hoy} onTareas={irTarea} onUmbrales={() => setUmbralesOpen(true)} />}
      {tab === 'plan' && <Planificador loc={loc} ixFc={ix} U={U} hoy={hoy} user={user} onUmbrales={() => setUmbralesOpen(true)} />}
      {tab === 'tareas' && <Tareas ix={ix} U={U} hoy={hoy} tarea={tarea} onTarea={setTarea} onUmbrales={() => setUmbralesOpen(true)} />}
      {tab === 'historial' && <Historial loc={loc} ixFc={ix} U={U} hoy={hoy} user={user} />}
      {tab === 'reporte' && <Reporte loc={loc} ixFc={ix} U={U} hoy={hoy} user={user} empresa={empresa} cfg={cfg} onCfg={setCfg} />}
      {tab === 'alertas' && (
        <div className="space-y-3 clima-in">
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-950">
            <b>Avisos de clima por WhatsApp o mail.</b> Elegí qué querés que te avisemos y a quién. Son los mismos destinatarios de Inteligencia › Alertas.
          </div>
          <AlertasClima feedID={user?.feedID} cam={user?.identifier} loc={loc} U={U}
            nota={<p className="mt-2 px-1 text-xs text-gray-500">Los avisos usan los criterios de esta obra. <button type="button" onClick={() => setUmbralesOpen(true)} className="font-semibold text-blue-700 hover:underline">Ajustar criterios</button></p>} />
        </div>
      )}
      <p className="text-[11px] text-gray-400 text-center pb-1">Datos meteorológicos: Open-Meteo · Criterios de día apto ajustables · {cfg.origen === 'local' ? 'Criterios guardados en este navegador' : 'Criterios guardados para esta obra'}</p>
    </div>
  );
};

export default WeatherComponent;
